import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'

// MUI Imports
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import MuiStepper from '@mui/material/Stepper'
import styled from '@mui/material/styles/styled'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import PreviewIcon from '@mui/icons-material/Preview'
import { Button } from '@mui/material'

// Component Imports
import StepperWrapper from '../atoms/StepperWrapper'
import FirmListManager from '../organisms/FirmListManager'
import TemplateSelection from '../organisms/TemplateSelection'
import Preview from '../organisms/Preview'

import { getFirms, getTemplates, sendEmail } from '../../../services/B2BServices'
import { getUrlParamsObj } from '../../../services/urlServices'
import { useGlobalStoreData } from '../../../../../store/globalStore'

// Styled Components
const Stepper = styled(MuiStepper)(({ theme }) => ({
  justifyContent: 'center',
  '& .MuiStep-root': {
    '& + i': {
      display: 'none',
      color: 'var(--mui-palette-text-secondary) !important'
    },
    '& .MuiStepLabel-label': {
      display: 'flex',
      cursor: 'pointer',
      alignItems: 'center',
      paddingBlock: theme.spacing(5),
      svg: {
        marginInline: theme.spacing(1.5),
        fill: 'var(--mui-palette-text-primary)'
      },
      '&.Mui-active, &.Mui-completed': {
        '& .MuiTypography-root': {
          color: 'var(--mui-palette-primary-main)'
        },
        '& svg': {
          fill: 'var(--mui-palette-primary-main)'
        }
      }
    },
    '&.Mui-completed + i': {
      color: 'var(--mui-palette-primary-main) !important'
    },
    [theme.breakpoints.up('md')]: {
      paddingBottom: 0,
      '& + svg': {
        display: 'block'
      },
      '& .MuiStepLabel-label': {
        display: 'block'
      }
    }
  }
}))

/*
  TODO:REFACTOR
  * getStepContent component haline getirilmeli (StepManager)
  * state management StepManager componentinde yapılmalı (activeStep hariç)
*/
const getStepContent = (
  filteredRegistryFirmsData,
  setFilteredRegistryFirmsData,
  serverSidePagination,
  setServerSidePagination,
  filteredSectors = [],
  setFilteredSectorsByKey,
  selectedTradeRegistrySectors,
  setSelectedTradeRegistrySectorsByKey,
  tradeRegistrySectors,
  tradeRegistryFirmKeys = [],
  step,
  firmKeys = [],
  activeFirmKey = null,
  setActiveFirmKey,
  b2bFirmsData = [],
  templates = [],
  rowSelection,
  setRowSelectionByKey,
  setSelectedFirmsByKey,
  fetching,
  selectedTemplate,
  setSelectedTemplate,
  onSubmit,
  b2bAccountId,
  getTemplatesWithSelected,
  nextLoading,
  additionalEmails,
  setAdditionalEmails,
  excelFirms,
  setExcelFirms
) => {
  switch (step) {
    case 0:
      return <FirmListManager
        filteredRegistryFirmsData={filteredRegistryFirmsData}
        setFilteredRegistryFirmsData={setFilteredRegistryFirmsData}
        serverSidePagination={serverSidePagination}
        setServerSidePagination={setServerSidePagination}
        filteredSectors={filteredSectors}
        setFilteredSectorsByKey={(val) => setFilteredSectorsByKey(activeFirmKey, val)}
        selectedTradeRegistrySectors={selectedTradeRegistrySectors?.[activeFirmKey] || []}
        setSelectedTradeRegistrySectors={(val) => setSelectedTradeRegistrySectorsByKey(activeFirmKey, val)}
        tradeRegistrySectors={tradeRegistrySectors?.[activeFirmKey] || []}
        firmKeys={firmKeys}
        activeFirmKey={activeFirmKey}
        setActiveFirmKey={setActiveFirmKey}
        data={b2bFirmsData?.[activeFirmKey] || []}
        setSelectedFirms={(val) => setSelectedFirmsByKey(activeFirmKey, val)}
        fetching={fetching}
        rowSelection={rowSelection?.[activeFirmKey] || {}}
        setRowSelection={(val) => setRowSelectionByKey(activeFirmKey, val)}
        additionalEmails={additionalEmails}
        setAdditionalEmails={setAdditionalEmails}
        excelFirms={excelFirms}
        setExcelFirms={setExcelFirms}
        isServerSide={tradeRegistryFirmKeys.includes(activeFirmKey)}
      />
    case 1:
      return <TemplateSelection
        templates={templates}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        b2bAccountId={b2bAccountId}
        getTemplatesWithSelected={getTemplatesWithSelected}
      />
    case 2:
      return <Preview
        selectedTemplate={selectedTemplate}
        onSubmit={onSubmit}
        nextLoading={nextLoading}
      />
    default:
      return null
  }
}

const Wizard = () => {
  const { t } = useTranslation()
  const queryParams = getUrlParamsObj()
  const navigate = useNavigate()

  const [activeStep, setActiveStep] = useState(0)
  const [rowSelection, setRowSelection] = useState({})// FirmList selection state
  const [b2bFirms, setB2bFirms] = useState({})
  const [tradeRegistryFirmKeys, setTradeRegistryFirmKeys] = useState([])
  const [selectedFirms, setSelectedFirms] = useState({})
  const [fetching, setFetching] = useState(true)
  const [nextLoading, setNextLoading] = useState(false)
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [additionalEmails, setAdditionalEmails] = useState([])
  const [excelFirms, setExcelFirms] = useState([])
  const [activeFirmKey, setActiveFirmKey] = useState(null)
  const [tradeRegistrySectors, setTradeRegistrySectors] = useState({})
  const [filteredSectors, setFilteredSectors] = useState({})
  const [selectedTradeRegistrySectors, setSelectedTradeRegistrySectors] = useState({})
  const [filteredRegistryFirmsData, setFilteredRegistryFirmsData] = useState({})
  const [serverSidePagination, setServerSidePagination] = useState({})

  const isAnyFirmSelected = !!([].concat(...Object.values(selectedTradeRegistrySectors)).concat(...Object.values(selectedFirms)))?.length

  const setFilteredSectorsByKey = (key, val) => {
    setFilteredSectors((prevState) => ({ ...prevState, [key]: val }))
  }

  const firmKeys = useMemo(() => {
    const b2bKeys = Object.keys(b2bFirms)
    const keys = Object.keys(tradeRegistrySectors)?.length ? b2bKeys.concat(tradeRegistryFirmKeys) : b2bKeys

    return keys
  }, [tradeRegistrySectors, b2bFirms])

  const languageGSD = useGlobalStoreData.language()

  const steps = [
    {
      title: t('mailTemplate.wizard.firmSelection'),
      icon: AddBusinessIcon
    },
    {
      title: t('mailTemplate.wizard.templateSelection'),
      icon: DashboardCustomizeIcon
    },
    {
      title: t('mailTemplate.wizard.preview'),
      icon: PreviewIcon
    }
  ]

  const nextStepToastErr = () => {
    const toastError = selectedTemplate ? t('mailTemplate.wizard.firmError') : t('mailTemplate.wizard.templateError')

    toast.error(toastError, {
      position: 'top-right',
      autoClose: true,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: false
    })
  }

  const handleNext = (step = null) => {
    if ((!isAnyFirmSelected && !additionalEmails?.length && !excelFirms?.length) || !selectedTemplate) {
      nextStepToastErr()
    } else {
      step >= 0 ? setActiveStep(step) : setActiveStep(activeStep + 1)
    }
  }

  const onSubmit = (formVal) => {
    if ((!isAnyFirmSelected && !additionalEmails?.length && !excelFirms?.length) || !selectedTemplate) {
      nextStepToastErr()
    } else {
      setNextLoading(true)

      const additionalEmailsArr = additionalEmails?.length
        ? [{
            emailList: [...additionalEmails],
            name: null,
            web: null
          }]
        : []

      const additionalFirmList = additionalEmailsArr.concat(excelFirms)

      const managedTradeRegistrySectorsData = Object.fromEntries(
        Object.entries(selectedTradeRegistrySectors).map(([key, arr]) => [
          key,
          arr.map(item => item.code)
        ])
      )

      const payload = {
        b2bCampaignId: queryParams.b2bCampaignId,
        templateId: selectedTemplate.id,
        subject: formVal.mailSubject,
        templateParameters: {},
        additionalFirmList,
        ...selectedFirms,
        tradeRegistrySectors: managedTradeRegistrySectorsData
      }

      sendEmail(payload)
        .then((res) => {
          toast.success(t('mailTemplate.wizard.emailSuccess'), {
            position: 'top-right',
            autoClose: true,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: false
          })

          const targetURL = `${process.env.PUBLIC_URL}/${languageGSD}/mailing?b2bAccountId=${queryParams.b2bAccountId}`
          navigate(targetURL)
        })
        .catch((err) => {
          toast.error(err || t('mailTemplate.wizard.emailError'), {
            position: 'top-right',
            autoClose: true,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: false
          })
        })
        .finally(() => {
          setNextLoading(false)
        })
    }
  }

  const setRowSelectionByKey = (key, callback) => {
    setRowSelection((prevState) => ({ ...prevState, [key]: callback(prevState[key]) }))
  }

  const setSelectedFirmsByKey = (key, val) => {
    if (key) {
      setSelectedFirms((prevState) => ({ ...prevState, [key]: val }))
    }
  }

  const setSelectedTradeRegistrySectorsByKey = (key, val) => {
    if (key) {
      setSelectedTradeRegistrySectors((prevState) => ({ ...prevState, [key]: val }))
    }
  }

  useEffect(() => {
    queryParams?.b2bCampaignId && getFirms({ b2bCampaignId: queryParams.b2bCampaignId }).then((res) => {
      setB2bFirms(res.data.b2bFirms)
      setTradeRegistryFirmKeys(Object.keys(res.data?.tradeRegistrySectors))
      setTradeRegistrySectors(res.data?.tradeRegistrySectors)
      setActiveFirmKey(Object.keys(res.data.b2bFirms)?.[0])
    }).finally(() => {
      setFetching(false)
    })
  }, [])

  const getTemplatesWithSelected = (selectedTemplateId = null) => {
    queryParams?.b2bAccountId && getTemplates({ b2bAccountId: queryParams.b2bAccountId }).then((res) => {
      setTemplates(res.data)

      const selected = selectedTemplateId ? res.data?.find((x) => x.id === selectedTemplateId) : res.data?.[0]
      setSelectedTemplate(selected)
    })
  }

  useEffect(() => {
    getTemplatesWithSelected()
  }, [languageGSD])

  const queryParamsError = (queryParams, b2bAccountIdError, b2bCampaignIdError) => {
    const b2bAccountId = queryParams?.b2bAccountId
    const b2bCampaignId = queryParams?.b2bCampaignId

    if (b2bAccountId && b2bCampaignId) {
      return false
    } else if (b2bCampaignId) {
      return <div>{b2bAccountIdError}</div>
    } else {
      return <div>{b2bCampaignIdError}</div>
    }
  }

  const stepContent = useMemo(() => getStepContent(
    filteredRegistryFirmsData,
    setFilteredRegistryFirmsData,
    serverSidePagination,
    setServerSidePagination,
    filteredSectors,
    setFilteredSectorsByKey,
    selectedTradeRegistrySectors,
    setSelectedTradeRegistrySectorsByKey,
    tradeRegistrySectors,
    tradeRegistryFirmKeys,
    activeStep,
    firmKeys,
    activeFirmKey,
    setActiveFirmKey,
    b2bFirms,
    templates,
    rowSelection,
    setRowSelectionByKey,
    setSelectedFirmsByKey,
    fetching,
    selectedTemplate,
    setSelectedTemplate,
    onSubmit,
    queryParams.b2bAccountId,
    getTemplatesWithSelected,
    nextLoading,
    additionalEmails,
    setAdditionalEmails,
    excelFirms,
    setExcelFirms
  ), [
    fetching,
    b2bFirms,
    activeFirmKey,
    rowSelection,
    activeStep,
    selectedTemplate,
    tradeRegistrySectors,
    filteredSectors,
    selectedTradeRegistrySectors,
    filteredRegistryFirmsData,
    serverSidePagination
  ])

  return (
    queryParamsError(queryParams, t('mailTemplate.wizard.b2bAccountIdError'), t('mailTemplate.wizard.b2bCampaignIdError')) ||
    <Card>
      <CardContent className='p-2' style={{ direction: 'ltr' }}>
        <StepperWrapper className='mb-4'>
          <Stepper
            activeStep={activeStep}
            connector={
              <ArrowForwardIosIcon className='mli-12 hidden md:block text-xl text-textDisabled'/>
            }
          >
            {steps.map((step, index) => {
              return (
                <Step key={index} onClick={() => handleNext(index)}>
                  <StepLabel icon={<></>} className='text-center'>
                    <step.icon sx={{ fontSize: 50 }}/>
                    <Typography className='step-title'>{step.title}</Typography>
                  </StepLabel>
                </Step>
              )
            })}
          </Stepper>
        </StepperWrapper>

        {activeStep + 1 < steps.length &&
          <div className='relative'>
            <Button variant='contained' onClick={handleNext} style={{ position: 'absolute', right: 0, bottom: 0 }}>
              {t('mailTemplate.wizard.firmList.nextBtn')}
            </Button>
          </div>
        }
      </CardContent>
      <Divider />

      {stepContent}
    </Card>
  )
}

export default Wizard
